import { defineStore } from "pinia";
import { ref, computed } from "vue";
import roleApi, { type Role, type RoleFormData } from "@/api/role";

export const useRoleStore = defineStore("role", () => {
    // State
    const roles = ref<Role[]>([]);
    const currentRole = ref<Role | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const totalPages = computed(() => meta.value?.last_page || 0);
    const paginatedData = computed(() => roles.value);
    const filteredRoles = computed(() => {
        if (!search.value) return roles.value;

        return roles.value.filter((role: Role) =>
            role.name.toLowerCase().includes(search.value.toLowerCase()),
        );
    });
    const hasRoles = computed(() => roles.value.length > 0);
    const isLoading = computed(() => loading.value);

    // Actions
    const fetchRoles = async (params?: {
        page?: number;
        per_page?: number;
        search?: string;
    }) => {
        loading.value = true;
        error.value = null;

        try {
            const response: any = await roleApi.getRoles({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                ...params,
            });

            roles.value = response.data?.items || response.data || [];
            if (response.data?.meta || response.meta) {
                meta.value = response.data?.meta || response.meta || null;
            }
        } catch (err: any) {
            // Don't set global error for validation errors (422) - let modal handle them
            if (err.response?.status !== 422) {
                error.value =
                    err.response?.data?.message || "Failed to fetch roles";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchRole = async (id: number) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await roleApi.getRole(id);
            currentRole.value = response.data;
            return currentRole.value;
        } catch (err: any) {
            // Don't set global error for validation errors (422) - let modal handle them
            if (err.response?.status !== 422) {
                error.value =
                    err.response?.data?.message || "Failed to fetch role";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createRole = async (data: RoleFormData) => {
        loading.value = true;
        error.value = null;

        try {
            const newRole = await roleApi.createRole(data);

            // Add to local list immediately to avoid refetch issues
            if (newRole.data) {
                roles.value.unshift(newRole.data);
            }

            return newRole;
        } catch (err: any) {
            // Don't set global error for validation errors (422) - let modal handle them
            if (err.response?.status !== 422) {
                error.value =
                    err.response?.data?.message || "Failed to create role";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateRole = async (id: number, data: RoleFormData) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedRole = await roleApi.updateRole(id, data);

            // Update role in local list
            const index = roles.value.findIndex((role: Role) => role.id === id);
            if (index !== -1) {
                roles.value[index] = updatedRole.data;
            }

            // Update current role if it's the one being edited
            if (currentRole.value?.id === id) {
                currentRole.value = updatedRole.data;
            }

            return updatedRole;
        } catch (err: any) {
            // Don't set global error for validation errors (422) - let modal handle them
            if (err.response?.status !== 422) {
                error.value =
                    err.response?.data?.message || "Failed to update role";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteRole = async (id: number) => {
        loading.value = true;
        error.value = null;

        try {
            await roleApi.deleteRole(id);

            // Remove from local list
            roles.value = roles.value.filter((role: Role) => role.id !== id);

            // Clear current role if it's the one being deleted
            if (currentRole.value?.id === id) {
                currentRole.value = null;
            }
        } catch (err: any) {
            // Don't set global error for validation errors (422) - let modal handle them
            if (err.response?.status !== 422) {
                error.value =
                    err.response?.data?.message || "Failed to delete role";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const assignPermissions = async (id: number, permissionIds: number[]) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await roleApi.assignPermissions(id, permissionIds);

            // Update role in local list
            const index = roles.value.findIndex((role: Role) => role.id === id);
            if (index !== -1) {
                // Refresh the role to get updated permissions
                const updatedRole = await roleApi.getRole(id);
                roles.value[index] = updatedRole.data;

                // Update current role if it's the one being edited
                if (currentRole.value?.id === id) {
                    currentRole.value = updatedRole.data;
                }
            }

            return response;
        } catch (err: any) {
            // Don't set global error for validation errors (422) - let modal handle them
            if (err.response?.status !== 422) {
                error.value =
                    err.response?.data?.message ||
                    "Failed to assign permissions";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
        fetchRoles();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
        fetchRoles();
    };

    const resetFilters = () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        fetchRoles();
    };

    return {
        // State
        roles,
        currentRole,
        loading,
        error,
        page,
        perPage,
        search,
        meta,

        // Getters
        totalPages,
        paginatedData,
        filteredRoles,
        hasRoles,
        isLoading,

        // Actions
        fetchRoles,
        fetchRole,
        createRole,
        updateRole,
        deleteRole,
        assignPermissions,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
